import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Form, Button, Spinner, OverlayTrigger, Tooltip, Modal, ProgressBar } from 'react-bootstrap';
import { BsPencil, BsTrash, BsThreeDotsVertical, BsCameraVideo, BsCheckCircleFill, BsXCircle, BsVolumeUp } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import StarDivider from '../components/uiComponents/StarDivider';
import Toast from '../components/Toast';
import Pagination from '../components/Pagination';
import MetadataSelect from '../components/MetadataSelect';
import videoService from '../api/videoService';
import { uploadFile } from '../api/uploadService';
import { VideoPreview } from '../components/VideoPlayers';

// Backend enums
const SECTION_OPTIONS = [
  { value: 'semence_matinale', label: 'Semence Matinale' },
  { value: 'enseignements', label: 'Enseignements' },
  { value: 'temoignages', label: 'Témoignages' },
  { value: 'prieres', label: 'Prières' },
  { value: 'motivation_lundi', label: 'Les Lundis de la motivation' },
];
const STATUT_OPTIONS = [
  { value: 'actif', label: 'Actif' },
  { value: 'inactif', label: 'Inactif' },
];
const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const EMPTY_FORM = {
  section: '',
  date: '',
  publication_date: '',
  theme: '',
  intervenant: '',
  lien_primaire: '',
  lien_second: '',
  recording_url: '',
  description: '',
  thumbnail_url: '',
  category: '',
  language: 'fr',
  is_featured: false,
  is_audio: true,
  is_video: false,
  statut: 'actif',
};

function extractError(err) {
  if (err?.response?.data?.errors?.length) {
    return err.response.data.errors.map((e) => e.message).join(', ');
  }
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

function isValidHttpUrl(value) {
  try {
    const url = new URL(value.trim());
    return (url.protocol === 'http:' || url.protocol === 'https:') && Boolean(url.hostname);
  } catch {
    return false;
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function VideosPage() {
  const userRole = useSelector((s) => s.auth.user?.role);
  const isViewer = userRole === 'viewer';
  const [videos, setVideos] = useState([]);
  const [years, setYears] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, currentPage: 1 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'add' | 'edit'
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });

  // Testimonial recording upload state
  const [recordingFile, setRecordingFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const recordingInputRef = useRef(null);

  const [filters, setFilters] = useState({
    section: '', theme: '', intervenant: '',
    month: '', year: '', week: '', statut: '',
    category: '', language: '', is_featured: '',
    page: 1, limit: 20,
  });

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  useEffect(() => {
    let isCurrent = true;

    const fetchFilterYears = async () => {
      try {
        const { data } = await videoService.getFilterYears();
        const yearList = data.data?.years ?? data.data ?? data.years ?? data;
        if (!Array.isArray(yearList)) {
          throw new Error('Format de réponse invalide pour les années');
        }
        const availableYears = yearList.map((item) =>
          String(typeof item === 'object' && item !== null ? item.year : item)
        );
        if (isCurrent) setYears(availableYears);
      } catch (err) {
        if (isCurrent) showToast(extractError(err), 'danger');
      }
    };

    fetchFilterYears();
    return () => {
      isCurrent = false;
    };
  }, []);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.section) params.section = filters.section;
      if (filters.theme) params.theme = filters.theme;
      if (filters.intervenant) params.intervenant = filters.intervenant;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;
      if (filters.week) params.week = filters.week;
      if (filters.statut) params.statut = filters.statut;
      if (filters.category) params.category = filters.category;
      if (filters.language) params.language = filters.language;
      if (filters.is_featured) params.is_featured = filters.is_featured;
      params.page = filters.page;
      params.limit = filters.limit;

      const { data } = await videoService.list(params);
      setVideos(data.data);
      setPagination(data.pagination || { total: 0, totalPages: 1, currentPage: 1 });
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const handleFilterChange = (e) =>
    setFilters((f) => ({ ...f, [e.target.name]: e.target.value, page: 1 }));

  const handleSearch = () => fetchVideos();

  const handleClearFilters = () => {
    setFilters((f) => ({
      ...f,
      section: '',
      theme: '',
      intervenant: '',
      month: '',
      year: '',
      week: '',
      statut: '',
      category: '',
      language: '',
      is_featured: '',
      page: 1,
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      // When switching away from testimonials, clear recording state
      if (name === 'section' && value !== 'temoignages') {
        next.recording_url = '';
      }
      // When section changes, reset theme and intervenant
      if (name === 'section') {
        next.theme = '';
        next.intervenant = '';
      }
      return next;
    });
    setFormErrors((fe) => ({
      ...fe,
      [name]: '',
      ...(name === 'section' ? { recording_url: '' } : {}),
    }));
    // Also reset file when section changes
    if (name === 'section') {
      setRecordingFile(null);
      setUploadProgress(0);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.section) errors.section = 'Section requise';
    if (!form.date) errors.date = 'Date requise';
    if (!form.publication_date) errors.publication_date = 'Date de publication requise';
    
    // For testimonials: theme and speaker are optional
    // For other sections: theme and speaker are mandatory
    if (form.section !== 'temoignages') {
      if (!form.theme) errors.theme = 'Thème requis';
      if (!form.intervenant) errors.intervenant = 'Intervenant requis';
    }
    
    // For testimonials: a selected file (not yet uploaded), an already-uploaded url, or a primary link all satisfy the requirement
    if (form.section === 'temoignages') {
      if (!recordingFile && !form.recording_url && !form.lien_primaire) {
        errors.recording_url = 'Un enregistrement ou un lien primaire est requis pour les témoignages';
      }
    } else {
      if (!form.lien_primaire) errors.lien_primaire = 'Lien primaire requis';
    }

    if (form.lien_primaire && !isValidHttpUrl(form.lien_primaire)) {
      errors.lien_primaire = 'Veuillez saisir une URL valide commençant par http:// ou https://';
    }
    if (form.lien_second && !isValidHttpUrl(form.lien_second)) {
      errors.lien_second = 'Veuillez saisir une URL valide commençant par http:// ou https://';
    }
    if (form.recording_url && !recordingFile && !isValidHttpUrl(form.recording_url)) {
      errors.recording_url = 'L’URL de l’enregistrement doit commencer par http:// ou https://';
    }

    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    try {
      // If a new recording file was selected, upload it to S3 first
      let finalRecordingUrl = form.recording_url;
      if (recordingFile) {
        setUploading(true);
        setUploadProgress(0);
        try {
          const { publicUrl } = await uploadFile('testimonials', recordingFile, setUploadProgress);
          finalRecordingUrl = publicUrl;
          setForm((f) => ({ ...f, recording_url: publicUrl }));
        } finally {
          setUploading(false);
        }
      }

      const payload = {
        section: form.section,
        date: form.date,
        publication_date: form.publication_date,
        theme: form.theme,
        intervenant: form.intervenant,
        lien_primaire: form.lien_primaire || null,
        lien_second: form.lien_second || null,
        recording_url: finalRecordingUrl || null,
        description: form.description || null,
        thumbnail_url: form.thumbnail_url || null,
        category: form.category || null,
        language: form.language || 'fr',
        is_featured: form.is_featured,
        statut: form.statut,
      };

      // Only include is_audio and is_video for testimonials section
      if (form.section === 'temoignages') {
        payload.is_audio = form.is_audio;
        payload.is_video = form.is_video;
      }

      if (view === 'add') {
        await videoService.create(payload);
        showToast('Vidéo ajoutée avec succès');
      } else {
        await videoService.update(editItem.id, payload);
        showToast('Vidéo mise à jour avec succès');
      }
      setView('list');
      setForm(EMPTY_FORM);
      setEditItem(null);
      setRecordingFile(null);
      setUploadProgress(0);
      await fetchVideos();
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleEditOpen = (video) => {
    setForm({
      section: video.section,
      date: video.date?.slice(0, 10) || '',
      publication_date: video.publication_date?.slice(0, 10) || '',
      theme: video.theme,
      intervenant: video.intervenant,
      lien_primaire: video.lien_primaire || '',
      lien_second: video.lien_second || '',
      recording_url: video.recording_url || '',
      description: video.description || '',
      thumbnail_url: video.thumbnail_url || '',
      category: video.category || '',
      language: video.language || 'fr',
      is_featured: video.is_featured ?? false,
      is_audio: video.is_audio ?? true,
      is_video: video.is_video ?? false,
      statut: video.statut,
    });
    setEditItem(video);
    setFormErrors({});
    setRecordingFile(null);
    setUploadProgress(0);
    handleClearFilters();
    setView('edit');
  };

  const handleDelete = async () => {
    try {
      await videoService.remove(deleteTarget.id);
      showToast('Vidéo supprimée avec succès');
      setDeleteTarget(null);
      await fetchVideos();
    } catch (err) {
      showToast(extractError(err), 'danger');
      setDeleteTarget(null);
    }
  };

  const sectionLabel = (val) =>
    SECTION_OPTIONS.find((s) => s.value === val)?.label || val;

  const monthFilterDisabled = !filters.year;
  const monthFilterTooltip = monthFilterDisabled ? 'Sélectionnez d’abord une année pour activer le filtre du mois.' : '';

  return (
    <Container fluid className="px-0">
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />

      <div className="text-center mb-4">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>ADMINISTRATIONS DES VIDEOS</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div
          className="d-md-none d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', marginBottom: filtersOpen ? 12 : 0 }}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <span className="fw-semibold" style={{ fontSize: 14 }}>Filtres de recherche</span>
          <span>{filtersOpen ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}</span>
        </div>
        <div className={filtersOpen ? '' : 'd-none d-md-block'}>
          <Row className="gy-1 gx-3">
            <Col md={10} xs={12}>
              <Row>
                <Col md={4} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Section</Form.Label>
                  <Form.Select 
                    name="section" 
                    value={filters.section} 
                    onChange={(e) => {
                      const val = e.target.value;
                      setFilters(f => ({ ...f, section: val, theme: '', intervenant: '', page: 1 }));
                    }}
                    size="sm"
                  >
                    <option value="">Toutes les sections</option>
                    {SECTION_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Form.Select>
                </Col>
                <Col md={4} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Thème</Form.Label>
                  <MetadataSelect
                    type="theme"
                    section={filters.section}
                    value={filters.theme}
                    onChange={val => setFilters(f => ({ ...f, theme: val, page: 1 }))}
                    placeholder="Tous les thèmes"
                    size="sm"
                    canAdd={false}
                    disabled={!filters.section}
                  />
                </Col>
                <Col md={4} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Intervenant</Form.Label>
                  <MetadataSelect
                    type="speaker"
                    theme={filters.section}
                    value={filters.intervenant}
                    onChange={val => setFilters(f => ({ ...f, intervenant: val, page: 1 }))}
                    placeholder="Tous les intervenants"
                    size="sm"
                    canAdd={false}
                    disabled={!filters.section}
                  />
                </Col>
                <Col md={4} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Statut</Form.Label>
                  <Form.Select name="statut" value={filters.statut} onChange={handleFilterChange} size="sm">
                    <option value="">Tous les statuts</option>
                    {STATUT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </Form.Select>
                </Col>
                <Col md={4} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Année</Form.Label>
                  <Form.Select
                    name="year"
                    value={filters.year}
                    onChange={handleFilterChange}
                    size="sm"
                  >
                    <option value="">Toutes</option>
                    {years.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </Form.Select>
                </Col>

                <Col md={4} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Mois</Form.Label>

                  {monthFilterDisabled ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="month-filter-tooltip">
                          {monthFilterTooltip}
                        </Tooltip>
                      }
                      trigger={['hover', 'focus']}
                    >
                      <span>
                        <Form.Select
                          name="month"
                          value={filters.month}
                          onChange={handleFilterChange}
                          size="sm"
                          disabled
                        >
                          <option value="">Tous</option>
                          {MONTHS.map((m, i) => (
                            <option key={m} value={i + 1}>
                              {m}
                            </option>
                          ))}
                        </Form.Select>
                      </span>
                    </OverlayTrigger>
                  ) : (
                    <Form.Select
                      name="month"
                      value={filters.month}
                      onChange={handleFilterChange}
                      size="sm"
                    >
                      <option value="">Tous</option>
                      {MONTHS.map((m, i) => (
                        <option key={m} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </Col>
                {/* <Col md={3} xs={12}>
                  <Form.Label className="small mb-0 fw-semibold">Mise en avant</Form.Label>
                  <Form.Select name="is_featured" value={filters.is_featured} onChange={handleFilterChange} size="sm">
                    <option value="">Tous</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </Form.Select>
                </Col> */}
              </Row>
            </Col>
            <Col md={2} xs={12} className="d-flex flex-md-column flex-row align-items-md-center align-items-end justify-content-center gap-2 mt-2 mt-md-0">
              {['section', 'theme', 'intervenant', 'month', 'year', 'week', 'statut', 'category', 'language', 'is_featured'].some(k => filters[k]) && (
                <Button onClick={handleClearFilters} variant="outline-secondary" size="sm" className="text-uppercase px-2" style={{ width: '7rem' }}>
                  EFFACER
                </Button>
              )}
              {/* {!isViewer && (
                <Button size="sm" style={{ backgroundColor: '#8b1512', border: 'none', width: '7rem' }}
                  onClick={() => { setForm(EMPTY_FORM); setFormErrors({}); setView('add'); }}>
                  + Nouvelle Vidéo
                </Button>
              )} */}
            </Col>
          </Row>
        </div>
      </div>

      {/* Add/Edit form */}
      {!isViewer && (view === 'add' || view === 'edit') && (
        <div style={{ maxWidth: 700, margin: '0 auto 24px' }}>
          <h6 className="fw-bold mb-2">
            {view === 'add' ? 'Ajouter une nouvelle vidéo' : 'Modifier la vidéo'}
          </h6>
          <Form className="bg-white rounded p-3" style={{ border: '1px solid #eee' }} onSubmit={handleSave}>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Section :</Form.Label>
              <Col sm={8}>
                <Form.Select name="section" value={form.section} onChange={handleFormChange} isInvalid={!!formErrors.section}>
                  <option value="">— Sélectionner la section —</option>
                  {SECTION_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
                <Form.Control.Feedback type="invalid">{formErrors.section}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Date :</Form.Label>
              <Col sm={8}>
                <Form.Control type="date" name="date" value={form.date} onChange={handleFormChange} isInvalid={!!formErrors.date} />
                <Form.Control.Feedback type="invalid">{formErrors.date}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Date de publication :</Form.Label>
              <Col sm={8}>
                <Form.Control type="date" name="publication_date" value={form.publication_date} onChange={handleFormChange} isInvalid={!!formErrors.publication_date} />
                <Form.Control.Feedback type="invalid">{formErrors.publication_date}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Thème : {form.section !== 'temoignages' && <span style={{ color: '#8b1512' }}>*</span>}
              </Form.Label>
              <Col sm={8}>
                <MetadataSelect
                  type="theme"
                  section={form.section}
                  value={form.theme}
                  onChange={val => { setForm(f => ({ ...f, theme: val })); setFormErrors(fe => ({ ...fe, theme: '' })); }}
                  placeholder="— Sélectionner un thème —"
                  isInvalid={!!formErrors.theme}
                  disabled={!form.section}
                />
                {formErrors.theme && <div className="text-danger" style={{ fontSize: 12, marginTop: 2 }}>{formErrors.theme}</div>}
                {!form.section && <Form.Text className="text-muted" style={{ fontSize: 11 }}>Veuillez d'abord sélectionner une section</Form.Text>}
                {form.section === 'temoignages' && <Form.Text className="text-muted" style={{ fontSize: 11 }}>Optionnel pour les témoignages</Form.Text>}
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                Intervenant : {form.section !== 'temoignages' && <span style={{ color: '#8b1512' }}>*</span>}
              </Form.Label>
              <Col sm={8}>
                <MetadataSelect
                  type="speaker"
                  theme={form.section}
                  value={form.intervenant}
                  onChange={val => { setForm(f => ({ ...f, intervenant: val })); setFormErrors(fe => ({ ...fe, intervenant: '' })); }}
                  placeholder="— Sélectionner un intervenant —"
                  isInvalid={!!formErrors.intervenant}
                  disabled={!form.section}
                />
                {formErrors.intervenant && <div className="text-danger" style={{ fontSize: 12, marginTop: 2 }}>{formErrors.intervenant}</div>}
                {!form.section && <Form.Text className="text-muted" style={{ fontSize: 11 }}>Veuillez d'abord sélectionner une section</Form.Text>}
                {form.section === 'temoignages' && <Form.Text className="text-muted" style={{ fontSize: 11 }}>Optionnel pour les témoignages</Form.Text>}
              </Col>
            </Form.Group>

            {/* Lien primaire — conditional based on section */}
            {form.section === 'temoignages' ? (
              <>
                {/* Recording file upload */}
                <Form.Group as={Row} className="mb-3 align-items-start">
                  <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                    Enregistrement <span style={{ color: '#8b1512' }}>*</span> :
                  </Form.Label>
                  <Col sm={8}>
                    {/* Existing recording saved on the record */}
                    {form.recording_url && !recordingFile && (
                      <div className="d-flex align-items-center gap-2 mb-2 p-2 rounded" style={{ backgroundColor: '#f0faf0', border: '1px solid #c3e6cb' }}>
                        <BsCheckCircleFill color="#28a745" size={14} />
                        <a href={form.recording_url} target="_blank" rel="noreferrer"
                          style={{ color: '#155724', fontSize: 12, wordBreak: 'break-all', flex: 1 }}>
                          {form.recording_url.split('/').pop()}
                        </a>
                        <BsXCircle
                          size={16} color="#8b1512" style={{ cursor: 'pointer', flexShrink: 0 }}
                          title="Supprimer l'enregistrement"
                          onClick={() => {
                            setForm((f) => ({ ...f, recording_url: '' }));
                            setFormErrors((fe) => ({ ...fe, recording_url: '' }));
                          }}
                        />
                      </div>
                    )}
                    {form.recording_url && !recordingFile && formErrors.recording_url && (
                      <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>{formErrors.recording_url}</div>
                    )}

                    {/* Drop zone — only shown when no recording saved yet */}
                    {!form.recording_url && (
                      <>
                        <div
                          onClick={() => recordingInputRef.current?.click()}
                          style={{
                            border: `2px dashed ${formErrors.recording_url ? '#dc3545' : '#ccc'}`,
                            borderRadius: 6, padding: '16px 12px', textAlign: 'center',
                            cursor: 'pointer', backgroundColor: '#fafafa',
                          }}
                        >
                          <BsCameraVideo size={24} color="#8b1512" className="mb-1" />
                          <div style={{ fontSize: 13, color: '#555' }}>
                            {recordingFile ? recordingFile.name : 'Cliquez pour sélectionner un fichier vidéo ou audio'}
                          </div>
                          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                            MP4, MOV, AVI, WebM, MP3, WAV
                          </div>
                        </div>
                        <input
                          ref={recordingInputRef}
                          type="file"
                          accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,audio/mpeg,audio/mp4,audio/wav,audio/webm"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setRecordingFile(file);
                              setUploadProgress(0);
                              setFormErrors((fe) => ({ ...fe, recording_url: '' }));
                            }
                            e.target.value = '';
                          }}
                        />
                        {formErrors.recording_url && (
                          <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>{formErrors.recording_url}</div>
                        )}
                      </>
                    )}

                    {/* Newly selected file chip */}
                    {recordingFile && (
                      <div className="d-flex align-items-center gap-2 mt-2 p-2 rounded" style={{ backgroundColor: '#fff8e1', border: '1px solid #ffe082' }}>
                        <BsCameraVideo size={14} color="#856404" />
                        <span style={{ fontSize: 12, flex: 1, wordBreak: 'break-all', color: '#856404' }}>
                          {recordingFile.name} ({(recordingFile.size / (1024 * 1024)).toFixed(1)} MB)
                        </span>
                        <BsXCircle
                          size={16} color="#8b1512" style={{ cursor: 'pointer', flexShrink: 0 }}
                          title="Retirer le fichier"
                          onClick={() => { setRecordingFile(null); setUploadProgress(0); }}
                        />
                      </div>
                    )}

                    {/* Upload progress bar */}
                    {uploading && (
                      <div className="mt-2">
                        <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated style={{ height: 8 }} />
                        <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Envoi en cours…</div>
                      </div>
                    )}
                  </Col>
                </Form.Group>

                {/* Optional external link for testimonials */}
                <Form.Group as={Row} className="mb-3 align-items-center">
                  <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>
                    Lien vidéo <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>(optionnel)</span> :
                  </Form.Label>
                  <Col sm={8}>
                    <Form.Control
                      name="lien_primaire" value={form.lien_primaire} onChange={handleFormChange}
                      placeholder="https://youtu.be/... (optionnel)" isInvalid={!!formErrors.lien_primaire}
                    />
                    <Form.Control.Feedback type="invalid">{formErrors.lien_primaire}</Form.Control.Feedback>
                    <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                      Vous pouvez fournir un lien en complément de l'enregistrement.
                    </Form.Text>
                  </Col>
                </Form.Group>

                {/* Audio/Video Type Checkboxes for Testimonials */}
                <Form.Group as={Row} className="mb-3 align-items-start">
                  <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Type de contenu :</Form.Label>
                  <Col sm={8}>
                    <div className="d-flex gap-4">
                      <Form.Check
                        type="radio"
                        label="Vidéo"
                        name="content_type"
                        checked={form.is_video && !form.is_audio}
                        onChange={() => {
                          setForm(f => ({ ...f, is_video: true, is_audio: false }));
                          setFormErrors(fe => ({ ...fe, is_video: '', is_audio: '' }));
                        }}
                        style={{ fontSize: 13 }}
                      />
                      <Form.Check
                        type="radio"
                        label="Audio"
                        name="content_type"
                        checked={form.is_audio && !form.is_video}
                        onChange={() => {
                          setForm(f => ({ ...f, is_audio: true, is_video: false }));
                          setFormErrors(fe => ({ ...fe, is_audio: '', is_video: '' }));
                        }}
                        style={{ fontSize: 13 }}
                      />
                    </div>
                    <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                      Sélectionnez le type de contenu pour ce témoignage. Audio est sélectionné par défaut.
                    </Form.Text>
                  </Col>
                </Form.Group>
              </>
            ) : (
              <Form.Group as={Row} className="mb-3 align-items-center">
                <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Lien primaire :</Form.Label>
                <Col sm={8}>
                  <Form.Control name="lien_primaire" value={form.lien_primaire} onChange={handleFormChange} placeholder="https://youtu.be/..." isInvalid={!!formErrors.lien_primaire} />
                  <Form.Control.Feedback type="invalid">{formErrors.lien_primaire}</Form.Control.Feedback>
                </Col>
              </Form.Group>
            )}

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Lien secondaire :</Form.Label>
              <Col sm={8}>
                <Form.Control name="lien_second" value={form.lien_second} onChange={handleFormChange} placeholder="https://youtu.be/... (optionnel)" isInvalid={!!formErrors.lien_second} />
                <Form.Control.Feedback type="invalid">{formErrors.lien_second}</Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={4} className="fw-semibold" style={{ fontSize: 14 }}>Statut :</Form.Label>
              <Col sm={8}>
                <Form.Select name="statut" value={form.statut} onChange={handleFormChange}>
                  {STATUT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Col>
            </Form.Group>

            <div className="text-center mt-4 d-flex gap-3 justify-content-center">
              <Button size="sm" variant="outline-secondary" type="button"
                style={{ letterSpacing: 1, paddingInline: 24 }}
                onClick={() => { setView('list'); setForm(EMPTY_FORM); setFormErrors({}); setRecordingFile(null); setUploadProgress(0); }}>
                ANNULER
              </Button>
              <Button size="sm" type="submit" disabled={saving}
                style={{ backgroundColor: '#8b1512', border: 'none', letterSpacing: 1, paddingInline: 40 }}>
                {saving ? <Spinner size="sm" animation="border" /> : view === 'add' ? 'ENREGISTRER' : 'ENREGISTRER LES MODIFICATIONS'}
              </Button>
            </div>
          </Form>
        </div>
      )}

      {/* List */}
      {view === 'list' && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">
              Résultats ({pagination.total ?? videos.length})
            </h6>
            {!isViewer && (
              <Button size="sm" style={{ backgroundColor: '#8b1512', border: 'none' }} onClick={() => { setForm(EMPTY_FORM); setFormErrors({}); handleClearFilters(); setView('add'); }}>
                + Nouvelle Vidéo
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8b1512' }} /></div>
          ) : videos.length === 0 ? (
            <div className="text-center py-5 text-secondary">Aucune vidéo trouvée.</div>
          ) : (
            <>
              <div className="d-none d-md-block table-mcm-wrap" style={{ overflowX: 'auto' }}>
                <table className="table-mcm">
                  <thead>
                    <tr>
                      <th className="th-mcm">Section</th>
                      <th className="th-mcm">Date</th>
                      <th className="th-mcm">Pub.</th>
                      <th className="th-mcm">Thème</th>
                      <th className="th-mcm">Intervenant</th>
                      <th className="th-mcm">Lien / Enreg.</th>
                      <th className="th-mcm">Lien secondaire</th>
                      <th className="th-mcm">Statut</th>
                      {!isViewer && <th className="th-mcm">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {videos.map((v) => (
                      <tr key={v.id}>
                        <td className="td-mcm">{sectionLabel(v.section)}</td>
                        <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>{formatDate(v.date)}</td>
                        <td className="td-mcm" style={{ whiteSpace: 'nowrap' }}>{formatDate(v.publication_date)}</td>
                        <td className="td-mcm">{v.theme}</td>
                        <td className="td-mcm">{v.intervenant}</td>
                        <td className="td-mcm">
                          {v.section === 'temoignages' && v.recording_url ? (
                            <a href={v.recording_url} style={{ color: '#8b1512', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }} target="_blank" rel="noreferrer">
                              {v.is_audio && !v.is_video ? (
                                <BsVolumeUp size={13} title="Audio" />
                              ) : v.is_video && !v.is_audio ? (
                                <BsCameraVideo size={13} title="Vidéo" />
                              ) : (
                                <BsCameraVideo size={13} title="Vidéo/Audio" />
                              )}
                              {v.is_audio && !v.is_video ? 'Enregistrement audio' : 'Enregistrement vidéo'}
                            </a>
                          ) : v.lien_primaire ? (
                            <a href={v.lien_primaire} style={{ color: '#8b1512', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }} target="_blank" rel="noreferrer">
                              {v.is_video && !v.is_audio ? (
                                <BsCameraVideo size={13} title="Vidéo" />
                              ) : (
                                <BsCameraVideo size={13} />
                              )}
                              {v.lien_primaire?.slice(0, 25)}…
                            </a>
                          ) : (
                            <span style={{ color: '#999', fontSize: 12 }}>—</span>
                          )}
                        </td>
                        <td className="td-mcm">
                          <a href={v.lien_second} style={{ color: '#8b1512', fontSize: 12 }} target="_blank" rel="noreferrer">
                            {v.lien_second?.slice(0, 30)}…
                          </a>
                        </td>
                        <td className="td-mcm">
                          <span className='text-capitalize' style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 11,
                            backgroundColor: v.statut === 'actif' ? '#d4edda' : '#f8d7da',
                            color: v.statut === 'actif' ? '#155724' : '#721c24',
                          }}>
                            {v.statut}
                          </span>
                        </td>
                        {!isViewer && (
                          <td className="td-mcm">
                            <div className="d-flex gap-2">
                              <BsPencil size={16} style={{ cursor: 'pointer', color: '#555' }} onClick={() => handleEditOpen(v)} />
                              <BsTrash size={16} style={{ cursor: 'pointer', color: '#8b1512' }} onClick={() => setDeleteTarget(v)} />
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-md-none">
                <div className="d-flex flex-column gap-3">
                  {videos.map((v) => (
                    <div key={v.id} className="bg-white rounded p-3" style={{ border: '1px solid #eee' }}>
                      <div className="d-flex justify-content-between border-bottom border-muted w-100 pb-3 align-items-start mb-3">
                        <div>
                          <div className="fw-bold">
                            {sectionLabel(v.section)}
                          </div>
                          <div className='text-muted fw-semibold' style={{ fontSize: 12 }}>
                            {formatDate(v.date)} • {formatDate(v.publication_date)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className='text-capitalize' style={{
                            padding: '2px 8px', borderRadius: 10, fontSize: 11,
                            backgroundColor: v.statut === 'actif' ? '#d4edda' : '#f8d7da',
                            color: v.statut === 'actif' ? '#155724' : '#721c24',
                            whiteSpace: 'nowrap',
                          }}>
                            {v.statut}
                          </span>
                          {!isViewer && (
                            <div style={{ position: 'relative' }}>
                              <BsThreeDotsVertical onClick={() => setOpenMenuId(openMenuId === v.id ? null : v.id)} size={18} color="#555" />
                              {openMenuId === v.id && (
                                <div
                                  style={{
                                    position: 'absolute', right: 0, top: '100%', zIndex: 100,
                                    backgroundColor: '#fff', border: '1px solid #e0e0e0',
                                    borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                    minWidth: 140, overflow: 'hidden', marginTop: 4,
                                  }}
                                >
                                  <button
                                    type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, cursor: 'pointer' }}
                                    onClick={() => { handleEditOpen(v); setOpenMenuId(null); }}
                                  >
                                    <BsPencil size={13} color="#555" /> Modifier
                                  </button>
                                  <button
                                    type="button"
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', background: 'none', border: 'none', fontSize: 13, color: '#8b1512', cursor: 'pointer' }}
                                    onClick={() => { setDeleteTarget(v); setOpenMenuId(null); }}
                                  >
                                    <BsTrash size={13} color="#8b1512" /> Supprimer
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 8 }}>
                        <div><span className="fw-semibold">Thème:&nbsp;&nbsp;</span>{v.theme}</div>
                        <div><span className="fw-semibold">Intervenant:&nbsp;&nbsp;</span>{v.intervenant}</div>
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        {v.section === 'temoignages' && v.recording_url ? (
                          <a href={v.recording_url} style={{ color: '#8b1512', fontSize: 13, wordBreak: 'break-word', display: 'flex', alignItems: 'center', gap: 6 }} target="_blank" rel="noreferrer">
                            {v.is_audio && !v.is_video ? (
                              <BsVolumeUp size={14} title="Audio" />
                            ) : v.is_video && !v.is_audio ? (
                              <BsCameraVideo size={14} title="Vidéo" />
                            ) : (
                              <BsCameraVideo size={14} title="Vidéo/Audio" />
                            )}
                            {v.is_audio && !v.is_video ? 'Enregistrement audio' : 'Enregistrement vidéo'}
                          </a>
                        ) : v.lien_primaire ? (
                          <a href={v.lien_primaire} style={{ color: '#8b1512', fontSize: 13, wordBreak: 'break-word', display: 'flex', alignItems: 'center', gap: 6 }} target="_blank" rel="noreferrer">
                            {v.is_video && !v.is_audio ? (
                              <BsCameraVideo size={14} title="Vidéo" />
                            ) : (
                              <BsCameraVideo size={14} />
                            )}
                            {v.lien_primaire}
                          </a>
                        ) : (
                          <span style={{ color: '#999', fontSize: 13 }}>—</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <Pagination
            currentPage={filters.page}
            totalPages={pagination.totalPages}
            onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          />
        </>
      )}

      {/* Video delete confirmation modal */}
      <Modal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        centered
        size="lg"
      >
        <Modal.Body style={{ padding: 28 }}>
          {/* <button onClick={() => setDeleteTarget(null)} className="modal-close-btn">×</button> */}

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px',
            }}>
              <BsTrash size={22} color="#8B0000" />
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer cette vidéo ?</h6>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>Cette action est irréversible.</p>
          </div>

          {deleteTarget && (
            <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 240px', flexShrink: 0, alignSelf: 'center' }}>
                <VideoPreview video={deleteTarget} compact />
              </div>
              <div style={{ flex: '1 1 200px', backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', fontSize: 13, minWidth: 0 }}>
                <div className="mb-2"><span className="fw-semibold">Section : </span>{sectionLabel(deleteTarget.section)}</div>
                <div className="mb-2"><span className="fw-semibold">Thème : </span>{deleteTarget.theme}</div>
                <div className="mb-2"><span className="fw-semibold">Intervenant : </span>{deleteTarget.intervenant}</div>
                <div className="mb-2"><span className="fw-semibold">Date : </span>{formatDate(deleteTarget.date)}</div>
                <div className="mb-2"><span className="fw-semibold">Publication : </span>{formatDate(deleteTarget.publication_date)}</div>
                {deleteTarget.lien_primaire && (
                  <div style={{ marginTop: 8 }}>
                    <a href={deleteTarget.lien_primaire} target="_blank" rel="noreferrer"
                      style={{ color: '#8b1512', wordBreak: 'break-all', fontSize: 12 }}>
                      {deleteTarget.lien_primaire}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
            <button
              onClick={() => setDeleteTarget(null)}
              style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}
            >
              ANNULER
            </button>
            <button
              onClick={handleDelete}
              style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13, letterSpacing: 0.5 }}
            >
              SUPPRIMER
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
}
